import { inject, Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    CalibrationModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigInstanceToken,
    ConfigInterface,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    CONNECTION_ERROR_CODES,
    CseStockService,
    ENDPOINTS,
    EscalationInstanceToken,
    EscalationInterface,
    getUuidV4,
    isDefined,
    LogUtilities,
    MaterialService,
    NOTIFICATION_STATUS_CODE,
    NotificationCompletedListModel,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationIdentifier,
    NotificationIdentifierModel,
    NotificationModel,
    NotificationStatusInstanceToken,
    notificationsUpdated$,
    NotificationUpdateService,
    ODataCalibrationModel,
    OnlineOnlyService,
    PaceState,
    RecipientsInstanceToken,
    RecipientsInterface,
    StatusModel,
    StorageNotificationStatusService,
    TextModel,
    TimeRecordingService,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
    UriUtilitiesService,
} from '@evorait/pace-core';
import {
    cleanupFormList,
    cleanupSpqDocs,
    ConfirmationInterface,
    FormIdentifier,
    FormsService,
    ParticipantCopyId,
    ParticipantParticulars,
} from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, exhaustMap, filter, finalize, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { LocalStorageSalesQuoteService } from '../../material/services/local-storage-sales-quote.service';
import { ConfirmationDataInterface } from '../interfaces/confirmation-data.interface';
import { OdataConfirmationService } from './odata-confirmation.service';
import { StorageConfirmationService } from './storage-confirmation.service';
import { cleanupSafetyAssessmentForm } from 'libs/pace-forms/src/lib/store/actions/safety-assessment.action';
import { PCHAlertService } from '../../core/services/pch-alerts/pch-alerts.service';

@Injectable({
    providedIn: 'root',
})
export class ConfirmationService implements ConfirmationInterface, ConfirmationDataInterface {
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);

    public readonly confirmationCompletedPreviously$: Observable<void>;
    private readonly confirmationCompletedPreviously = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly formsService: FormsService,
        private readonly odataConfirmationService: OdataConfirmationService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly materialService: MaterialService,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        private readonly timeRecordingService: TimeRecordingService,
        @Inject(EscalationInstanceToken) private readonly escalationService: EscalationInterface,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        private readonly cseStockService: CseStockService,
        private readonly store: Store<PaceState>,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly storageNotificationStatusService: StorageNotificationStatusService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(RecipientsInstanceToken) private readonly recipientsService: RecipientsInterface,
        private readonly storageConfirmationService: StorageConfirmationService,
        private readonly pchAlertsService: PCHAlertService,
    ) {
        this.confirmationCompletedPreviously$ = this.confirmationCompletedPreviously.asObservable();
        notificationsUpdated$()
            .pipe(exhaustMap(() => this.storageConfirmationService.purgeInvalidConfirmations()))
            .subscribe();
    }

    public getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel> {
        this.log.debug(
            `ConfirmationService: getConfirmation: loading confirmation "${id.toString()}" by choosing local or remote copy.`,
        );
        return this.timesheetAndExpensesService.selectCurrentTimesheet$({ once: true }).pipe(
            mergeMap((timesheet) =>
                this.storageConfirmationService.getConfirmation(id, type, timesheet).pipe(
                    catchError((error) => {
                        this.log.debug(
                            `ConfirmationService: getConfirmation: Creating new confirmation model since it was not possible to load one:`,
                            JSON.stringify(error),
                        );
                        // make sure start-of-work is set. Usually this would be ensured by the travel-status-update, but if the app
                        // was deleted after sending OSR and then reinstalled we could end up in a confirmation without start-of-work
                        // having been set. Since we will access it when doing the time-recording, this is the last place we can make
                        // sure it is set.
                        if (!timesheet.startOfWork.isValid()) {
                            // set start-of-work to now. Since we assume localStorage was deleted, there is no use in checking for
                            // other, earlier notifications here
                            this.timesheetAndExpensesService
                                .saveStartOfWork(moment().startOf('minute'))
                                .pipe(take(1))
                                .subscribe();
                        }
                        return of(new ConfirmationModel(id, type, timesheet));
                    }),
                ),
            ),
            map((confirmation) => {
                this.log.debug('ConfirmationService: getConfirmation: Loading saved confirmation');
                // check for confirmation.materials as it could be undefined for APPS user
                if (confirmation.materials && confirmation.materials.listMaterial) {
                    confirmation.materials.listMaterial = this.materialService.updateMaterialListFromLocalStorage(
                        confirmation.notificationIdentifier,
                        confirmation.materials.listMaterial,
                    );
                }
                return confirmation;
            }),
            switchMap((confirmation) =>
                this.storageNotificationStatusService.saveConfirmationStarted(id).pipe(map(() => confirmation)),
            ),
        );
    }

    public saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel> {
        confirmation.saveTimestamp = Date.now();

        this.log.debug(`${this.constructor.name}: saveConfirmation: saving confirmation`, {
            confirmation: confirmation.notificationIdentifier.toString(),
            timerecording: confirmation.timeRecording.timeRecordingEntries.getOutModel(),
            timestamp: confirmation.saveTimestamp,
        });
        return this.storageConfirmationService.saveConfirmation(confirmation).pipe(
            mergeMap(() => this.saveMaterials(confirmation)),
            mergeMap(() => {
                if (
                    this.configService.getShowDataMaintenanceEnhancement() &&
                    typeof confirmation.serviceReport !== 'undefined'
                ) {
                    return this.recipientsService.saveRecipientsByNotificationId(
                        confirmation.serviceReport.existingRecipients.value,
                        confirmation.notificationIdentifier,
                    );
                }
                return of(undefined);
            }),
            mergeMap(() => {
                if (this.configService.getAllowNotificationPause()) {
                    const afterHourValue = this.getIsAfterHoursEnabled(confirmation);
                    const notification = this.notificationService.getNotificationByIdentifier(
                        confirmation.notificationIdentifier,
                    );
                    if (!notification) {
                        throw new Error(
                            `Could not find notification for identifier '${confirmation.notificationIdentifier.toString()}'`,
                        );
                    }
                    return this.timesheetAndExpensesService.saveTimesheetNotification(
                        notification,
                        [],
                        confirmation.timeRecording.timeRecordingEntries.value,
                        new TextModel('Distance'),
                        afterHourValue,
                    );
                }
                return of(undefined);
            }),
            map(() => confirmation),
        );
    }

    private getIsAfterHoursEnabled(confirmation: ConfirmationModel): boolean {
        const isAfterHoursEnabled = this.configService.getEnabledAfterHoursCallOut();
        const afterHourValue = isAfterHoursEnabled
            ? (confirmation.timeRecording?.afterHourCallOut?.model?.value ?? false)
            : false;
        return afterHourValue;
    }

    /**
     * Save expenses, distance and time recording of a notification to the current timesheet
     *
     * @returns void event
     * @throws Error for missing time recording section
     * @param confirmation
     * @param notification
     * @private
     */
    private saveTimeSheetAndExpenses(
        confirmation: ConfirmationModel,
        notification: NotificationModel,
    ): Observable<void> {
        if (!confirmation.timeRecording) {
            throw new Error(`Confirmation did not contain a time recording section!`);
        }
        const expenses =
            confirmation.expenses && confirmation.expenses.expenses ? confirmation.expenses.expenses.value : [];
        const distance =
            confirmation.expenses && confirmation.expenses.distance
                ? confirmation.expenses.distance
                : new TextModel('Distance');
        const afterHourValue = this.getIsAfterHoursEnabled(confirmation);
        this.log.debug(`${this.constructor.name}: saveTimeSheetAndExpenses`, {
            confirmation: confirmation.notificationIdentifier.toString(),
            timerecording: confirmation.timeRecording.getOutModel(),
            notificationId: notification.identifier,
            distance: distance.value,
            expenses: expenses.map((exp) => exp.getOutModel()),
            afterHourValue,
        });
        return this.timesheetAndExpensesService
            .saveTimesheetNotification(
                notification,
                expenses,
                confirmation.timeRecording.timeRecordingEntries.value,
                distance,
                afterHourValue,
            )
            .pipe(map(() => undefined));
    }

    private filterConfirmationStatusComplete(notificationId: string): Observable<NotificationCompletedListModel> {
        return this.loadRecentlyCompletedConfirmations().pipe(
            tap((completedNotifications) => {
                this.log.debug(
                    `${this.constructor.name}: filterConfirmationStatusComplete: RecentlyCompletedConfirmations`,
                    LogUtilities.getLogOutputCompletedNotifications(completedNotifications),
                );
            }),
            filter((completedNotifications) => {
                if (!completedNotifications.completedNotifications[notificationId]) {
                    this.log.debug(
                        `${this.constructor.name}: filterConfirmationStatusComplete: confirmation not yet completed`,
                        notificationId,
                    );
                    return true;
                }

                this.log.warn(
                    `${this.constructor.name}: filterConfirmationStatusComplete: confirmation completed previously`,
                    notificationId,
                );
                this.confirmationCompletedPreviously.next();
                return false;
            }),
        );
    }

    public submitConfirmation(confirmation: ConfirmationModel): Observable<void> {
        const now = moment();
        const notification = this.notificationService.getNotificationByIdentifier(confirmation.notificationIdentifier);

        if (!notification) {
            throw new Error(
                `Could not find notification for identifier '${confirmation.notificationIdentifier.toString()}'`,
            );
        }

        this.log.debug(`${this.constructor.name}: submitConfirmation: start submission`, {
            outModel: confirmation.getOutModel(),
            now: now.toISOString(),
            notification: notification.identifier.toString(),
        });
        return this.filterConfirmationStatusComplete(notification.identifier.toString()).pipe(
            mergeMap(() => this.saveConfirmation(confirmation)),
            mergeMap(() => this.odataConfirmationService.saveConfirmation(confirmation)),
            catchError((error) => {
                this.log.error(
                    `ConfirmationService: submitConfirmation:
                        ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                        : <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_TITLE'),
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                        : <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_BODY'),
                );
                return throwError(() => new Error(error));
            }),
            // update notification status to CRM and save in localStorage
            mergeMap(() => {
                const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now);
                return this.notificationService.saveStatus(notification, statusModel);
            }),
            // cleanup runs before the status check so that removeConfirmation is always called
            // even when saveStatus did not persist the expected CRM status
            mergeMap(() => this.postConfirmationSubmitCleanup(confirmation, notification)),
            // check if notification status is not saved correctly — throw after cleanup so the
            // caller is aware of the inconsistency without leaving the confirmation in storage
            tap(() => {
                const status = this.notificationService.getStatus(notification).statusCode;
                if (status !== NOTIFICATION_STATUS_CODE.CRM) {
                    throw new Error(
                        `ConfirmationService: submitConfirmation: saveStatus: Notification status should be ${NOTIFICATION_STATUS_CODE.CRM} instead of ${status}`,
                    );
                }
            }),
        );
    }

    public confirmationCompletedPreviouslyCleanup(confirmation: ConfirmationModel): Observable<boolean> {
        const potentialNotification = this.notificationService.getNotificationByIdentifier(
            confirmation.notificationIdentifier,
        );
        if (!potentialNotification) {
            return this.storageConfirmationService.removeConfirmation(confirmation);
        }
        const notification = potentialNotification;
        this.storageNotificationStatusService.removeConfirmationStarted(confirmation.notificationIdentifier);
        const now = moment();
        const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now);
        return this.notificationService
            .saveStatus(notification, statusModel)
            .pipe(mergeMap(() => this.storageConfirmationService.removeConfirmation(confirmation)));
    }

    private postConfirmationSubmitCleanup(
        confirmation: ConfirmationModel,
        notification: NotificationModel,
    ): Observable<void> {
        const run = getUuidV4();
        const mainEquipment = notification.getMainEquipment();
        if (!isDefined(mainEquipment)) {
            throw new Error(
                `ConfirmationService: postConfirmationSaveAndCleanup: Notification '${confirmation.notificationIdentifier.toString()}' has no main equipment!`,
            );
        }
        // remove successful submitted confirmation
        confirmation.isSubmitted = true;
        this.storageNotificationStatusService.removeConfirmationStarted(confirmation.notificationIdentifier);
        this.log.debug(`${this.constructor.name}: postConfirmationSubmitCleanup`, {
            notificationId: notification.identifier.toJSON(),
            run,
        });
        return (
            this.storageConfirmationService
                .removeConfirmation(confirmation)
                .pipe(
                    mergeMap(() => this.notificationStatusService.addNotificationToRecentlyCompletedList(notification)),
                    mergeMap(() => this.saveMaterials(confirmation)),
                    mergeMap(() => this.notificationService.handlePausedNotifications()),
                    mergeMap(() => this.saveTimeSheetAndExpenses(confirmation, notification)),
                )
                // Multiple pipe statements because rxjs operator chaining only supports 9 operators for type inference
                // https://github.com/ReactiveX/rxjs/issues/4221#issuecomment-426774631
                .pipe(
                    mergeMap(() => this.recipientsService.removeRecipientsFromLocalStorage(notification.identifier)),
                    // stop escalation timeout
                    // remove all what's-new for notification
                    // remove the cancellation-confirmation information for this notification
                    // remove locally saved sales quote information
                    // P58: update bootstock materials
                    mergeMap(async () => {
                        this.log.debug(
                            `${this.constructor.name}: postConfirmationSubmitCleanup: removeEscalationTimer, removeNotificationUpdates, removeCancellationConfirmationForInprogress, removeSalesQuoteFromLocalStorageAsync, update bootstock`,
                            { run },
                        );
                        this.escalationService.removeEscalationTimer(notification);
                        this.notificationUpdateService.removeNotificationUpdates(notification.identifier);
                        this.notificationService.removeCancellationConfirmationForInprogress();
                        if (this.clientManager.isNgmP58()) {
                            await this.localStorageSalesQuoteService
                                .removeSalesQuoteFromLocalStorageAsync(notification.identifier)
                                .catch((e) =>
                                    this.log.error(
                                        this.constructor.name,
                                        'failed to remove sales quote from local storage',
                                        JSON.stringify(e),
                                    ),
                                );
                            const bootStockMaterials = this.materialService.getAllBootStockMaterialsForNotification(
                                notification.identifier,
                            );
                            this.cseStockService.setLocalBootStockMaterialModels(bootStockMaterials);
                        }
                    }),
                    // cleanup downloaded forms
                    mergeMap(() => {
                        this.log.debug(`${this.constructor.name}: postConfirmationSubmitCleanup: cleanup forms`, {
                            run,
                        });
                        if (this.clientManager.isNgmP58() || notification.isPaceFormsEnabled) {
                            return this.formsService
                                .getFormList(
                                    mainEquipment.matnr.value,
                                    mainEquipment.equnr.value,
                                    {
                                        frontId: notification.identifier.frontId,
                                        qmNum: notification.identifier.qmNum,
                                        maNum: notification.identifier.maNum,
                                    },
                                    true,
                                )
                                .pipe(
                                    take(1),
                                    filter(isDefined),
                                    map((formList) => {
                                        this.store.dispatch(cleanupFormList({ formList }));
                                        return undefined;
                                    }),
                                    catchError(() => {
                                        this.log.debug(
                                            `ConfirmationService: postConfirmationSaveAndCleanup: Form list cleanup not necessary since there is no list`,
                                        );
                                        return of(undefined);
                                    }),
                                    finalize(() => {
                                        // Logic to clean up all the SPQ data for the notification
                                        if (
                                            this.configService.getDisplayServicePartQuestionnaire() &&
                                            confirmation.materials &&
                                            confirmation.materials.listMaterial.value.length > 0
                                        ) {
                                            const spqMaterials = confirmation.materials.listMaterial.value
                                                .filter((material) => material.showServicePartQuestionnaire())
                                                .map((material) => {
                                                    const form: FormIdentifier = {
                                                        equipmentId: mainEquipment.equnr.value,
                                                        formId: material.spqDocId,
                                                        materialId: material.materialIdentifier.materialNumber.value,
                                                    };
                                                    const notificationId: NotificationIdentifier = {
                                                        qmNum: material.notificationIdentifier.qmNum,
                                                        maNum: material.notificationIdentifier.maNum,
                                                        frontId: material.notificationIdentifier.frontId,
                                                    };
                                                    return { form, notificationId, materialKey: material.materialKey };
                                                });

                                            this.log.debug(
                                                `ConfirmationService: postConfirmationSaveAndCleanup: cleaning up spq data`,
                                            );
                                            this.store.dispatch(cleanupSpqDocs({ spqMaterials }));
                                        }
                                    }),
                                );
                        }
                        return of(undefined);
                    }),
                    mergeMap(() => {
                        this.log.debug(
                            `${this.constructor.name}: postConfirmationSubmitCleanup: cleanup safety assessment`,
                            {
                                run,
                            },
                        );
                        // Logic to clean up all the Safety assessment form data for the notification
                        const safetyAssessmentFormId = this.configService.getSafetyAssessmentForm();
                        const formIdentifier = {
                            formId: safetyAssessmentFormId,
                            equipmentId: mainEquipment?.equnr.value,
                            materialId: mainEquipment?.matnr.value,
                        };

                        return this.formsService
                            .checkSafetyAssessmentFormInCache(formIdentifier, notification.identifier)
                            .pipe(
                                map((hasSAFormInCache) => {
                                    if (hasSAFormInCache) {
                                        this.store.dispatch(
                                            cleanupSafetyAssessmentForm({
                                                form: formIdentifier,
                                                notification: notification.identifier,
                                            }),
                                        );
                                    }
                                    return undefined;
                                }),
                            );
                    }),
                    tap(() => {
                        if (this.configService.getCentricarePCHEnabled()) {
                            this.log.debug(
                                `${this.constructor.name}: postConfirmationSubmitCleanup: cleanUpAlertPostCRM`,
                                {
                                    run,
                                },
                            );
                            this.pchAlertsService.cleanUpAlertPostCRM(notification.identifier);
                        }
                    }),
                    catchError((error) => {
                        this.log.error(
                            `ConfirmationService: postConfirmationSubmitCleanup: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return of(undefined);
                    }),
                )
        );
    }

    /**
     * save updated materials from confirmation screen to storage
     *
     * @param confirmation the confirmation model
     * @returns {Observable<unknown>} observable that completes when the materials are saved
     * @throws {Error} if the notification is not found
     */
    private saveMaterials(confirmation: ConfirmationModel): Observable<void> {
        this.log.debug(`${this.constructor.name}: saveMaterials`, {
            numMaterials: confirmation.materials?.listMaterial?.value?.length,
        });
        if (!confirmation.materials || confirmation.materials.listMaterial.value.length === 0) {
            // no materials - no saving
            return of(undefined);
        }
        const notification = this.notificationService.getNotificationByIdentifier(confirmation.notificationIdentifier);

        if (!notification) {
            throw new Error(
                `Unable to load notification for confirmation of notification '${confirmation.notificationIdentifier.toString()}'`,
            );
        }

        const isConfirmationSubmitted =
            this.notificationService.getStatus(notification).statusCode === NOTIFICATION_STATUS_CODE.CRM;

        // The activity date shall be the OSR End date if available, else the date where the CRM was send to SAP, for material history req.
        let materialConsumedDate = moment();
        const osrEntries = this.timeRecordingService.getOSREntries(
            confirmation.timeRecording.timeRecordingEntries.value,
        );
        if (osrEntries.length > 0) {
            const lastEntry = this.timeRecordingService.getLastTimeRecordingEntry(osrEntries);
            if (lastEntry) {
                materialConsumedDate = lastEntry.endDateTime.clone();
            }
        }

        return this.materialService
            .saveConfirmationMaterialsToLocalStorage(
                confirmation.materials.listMaterial.value,
                isConfirmationSubmitted,
                materialConsumedDate,
            )
            .pipe(map(() => undefined));
    }

    // this function is being called when the user is removing a participant from pace forms.
    // We need to remove the corresponding attendee from the attendee list for the participant
    public removeAttendee(): Observable<ParticipantCopyId> {
        return throwError(() => `ConfirmationService: removeAttendee: Method should be called only from PACE US`);
    }

    public saveAttendee(): Observable<void> {
        return throwError(() => `ConfirmationService: saveAttendee: Method should be called only from PACE US`);
    }

    // Only used in PACE US
    public getParticipantsFromAttendees(): Observable<ParticipantParticulars[]> {
        return throwError(
            () => `ConfirmationService: getParticipantsFromAttendees: Method should be called only from PACE US`,
        );
    }

    public getCalibrationDate(measurementToolId: string): Observable<CalibrationModel> {
        let query = ENDPOINTS.MeasToolSet.toString();
        const queryFilter = `Equipment eq '${measurementToolId}'`;
        query = this.uriUtilities.appendFilterValue(query, queryFilter);
        return this.onlineOnlyService.get(query).pipe(
            map((response: ODataCalibrationModel[]) => {
                if (response.length < 1) {
                    throw new Error(`ConfirmationService: getCalibrationDate: Could not get Calibration Date!`);
                }
                return CalibrationModel.fromODataCalibrationDate(response[0]);
            }),
        );
    }

    private loadRecentlyCompletedConfirmations(): Observable<NotificationCompletedListModel> {
        return this.notificationStatusService.loadRecentlyCompletedNotifications().pipe(take(1));
    }

    public triggerConfirmationCompletedPreviously(): void {
        this.confirmationCompletedPreviously.next();
    }

    public signLater(notification: NotificationModel): void {
        const now = moment();
        let nextStatus: NOTIFICATION_STATUS_CODE;
        switch (notification.updatedStatus.statusCode) {
            case NOTIFICATION_STATUS_CODE.ROSR:
                nextStatus = NOTIFICATION_STATUS_CODE.RTOBS;
                break;
            case NOTIFICATION_STATUS_CODE.FCC:
                nextStatus = NOTIFICATION_STATUS_CODE.FTOBS;
                break;
            case NOTIFICATION_STATUS_CODE.RTOBS:
            case NOTIFICATION_STATUS_CODE.FTOBS:
                nextStatus = notification.updatedStatus.statusCode;
                break;
            default:
                nextStatus = NOTIFICATION_STATUS_CODE.TOBS;
                break;
        }
        const statusModel = new StatusModel(nextStatus, now, now);
        this.notificationService
            .saveStatus(notification, statusModel)
            .pipe(
                mergeMap(() => this.notificationService.savePausedIndicator([notification], false)),
                catchError((error) => {
                    this.log.error(
                        'Error while saving notification status for sign later: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
                take(1),
            )
            .subscribe();
    }
}
